/**
 * AdminMetricsPage Reducer
 *
 * Consolidates 15+ useState calls into a single useReducer for clearer
 * state transitions and grouped state slices.
 */

import type {
  MetricsReport,
  OperationReport,
  IndividualOperation,
  UserBreakdown,
  CountryBreakdown,
  OperationCategories,
} from '../../services/metricsAPI';
import type { TimePreset } from './components/MetricsFilterBar';

export type ViewMode = 'summary' | 'individual' | 'users' | 'countries';

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

export interface MetricsState {
  // Common state
  loading: boolean;
  error: string | null;
  environment: string | undefined;

  // Filter state
  activeView: ViewMode;
  timePreset: TimePreset;
  customStart: string;
  customEnd: string;
  selectedCategory: string | null;
  userFilter: string;
  countryFilter: string;
  categories: OperationCategories | null;
  authToken: string;

  // Summary view state
  report: MetricsReport | null;
  selectedOperation: string | null;
  operationReport: OperationReport | null;
  loadingDetail: boolean;

  // Individual ops state
  individualOps: IndividualOperation[];
  opsLoading: boolean;
  opsCursor: string | null;

  // Users view state
  userBreakdown: UserBreakdown[];
  usersLoading: boolean;

  // Countries view state
  countryBreakdown: CountryBreakdown[];
  countriesLoading: boolean;
}

export const initialMetricsState: MetricsState = {
  loading: true,
  error: null,
  environment: undefined,

  activeView: 'summary',
  timePreset: null,
  customStart: '',
  customEnd: '',
  selectedCategory: null,
  userFilter: '',
  countryFilter: '',
  categories: null,
  authToken: '',

  report: null,
  selectedOperation: null,
  operationReport: null,
  loadingDetail: false,

  individualOps: [],
  opsLoading: false,
  opsCursor: null,

  userBreakdown: [],
  usersLoading: false,

  countryBreakdown: [],
  countriesLoading: false,
};

// ---------------------------------------------------------------------------
// Action types
// ---------------------------------------------------------------------------

export type MetricsAction =
  // Common
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_ENVIRONMENT'; environment: string | undefined }
  | { type: 'SET_AUTH_TOKEN'; token: string }
  // Filters
  | { type: 'SET_ACTIVE_VIEW'; view: ViewMode }
  | { type: 'SET_TIME_PRESET'; preset: TimePreset }
  | { type: 'SET_CUSTOM_START'; start: string }
  | { type: 'SET_CUSTOM_END'; end: string }
  | { type: 'SET_SELECTED_CATEGORY'; category: string | null }
  | { type: 'SET_USER_FILTER'; filter: string }
  | { type: 'CLEAR_USER_FILTER' }
  | { type: 'SET_COUNTRY_FILTER'; filter: string }
  | { type: 'CLEAR_COUNTRY_FILTER' }
  | { type: 'SET_CATEGORIES'; categories: OperationCategories }
  // Summary view
  | { type: 'SET_REPORT'; report: MetricsReport }
  | { type: 'SET_SELECTED_OPERATION'; operation: string | null }
  | { type: 'SET_OPERATION_REPORT'; report: OperationReport | null }
  | { type: 'SET_LOADING_DETAIL'; loading: boolean }
  // Individual ops
  | { type: 'SET_INDIVIDUAL_OPS'; ops: IndividualOperation[]; append: boolean }
  | { type: 'SET_OPS_LOADING'; loading: boolean }
  | { type: 'SET_OPS_CURSOR'; cursor: string | null }
  // Users
  | { type: 'SET_USER_BREAKDOWN'; breakdown: UserBreakdown[] }
  | { type: 'SET_USERS_LOADING'; loading: boolean }
  // Countries
  | { type: 'SET_COUNTRY_BREAKDOWN'; breakdown: CountryBreakdown[] }
  | { type: 'SET_COUNTRIES_LOADING'; loading: boolean };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export function metricsReducer(state: MetricsState, action: MetricsAction): MetricsState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.loading };

    case 'SET_ERROR':
      return { ...state, error: action.error };

    case 'SET_ENVIRONMENT':
      return { ...state, environment: action.environment };

    case 'SET_AUTH_TOKEN':
      return { ...state, authToken: action.token };

    case 'SET_ACTIVE_VIEW':
      return { ...state, activeView: action.view };

    case 'SET_TIME_PRESET':
      return { ...state, timePreset: action.preset };

    case 'SET_CUSTOM_START':
      return { ...state, customStart: action.start };

    case 'SET_CUSTOM_END':
      return { ...state, customEnd: action.end };

    case 'SET_SELECTED_CATEGORY':
      return { ...state, selectedCategory: action.category };

    case 'SET_USER_FILTER':
      return { ...state, userFilter: action.filter };

    case 'CLEAR_USER_FILTER':
      return { ...state, userFilter: '' };

    case 'SET_COUNTRY_FILTER':
      return { ...state, countryFilter: action.filter };

    case 'CLEAR_COUNTRY_FILTER':
      return { ...state, countryFilter: '' };

    case 'SET_CATEGORIES':
      return { ...state, categories: action.categories };

    case 'SET_REPORT':
      return { ...state, report: action.report };

    case 'SET_SELECTED_OPERATION':
      return { ...state, selectedOperation: action.operation };

    case 'SET_OPERATION_REPORT':
      return { ...state, operationReport: action.report };

    case 'SET_LOADING_DETAIL':
      return { ...state, loadingDetail: action.loading };

    case 'SET_INDIVIDUAL_OPS':
      return {
        ...state,
        individualOps: action.append ? [...state.individualOps, ...action.ops] : action.ops,
      };

    case 'SET_OPS_LOADING':
      return { ...state, opsLoading: action.loading };

    case 'SET_OPS_CURSOR':
      return { ...state, opsCursor: action.cursor };

    case 'SET_USER_BREAKDOWN':
      return { ...state, userBreakdown: action.breakdown };

    case 'SET_USERS_LOADING':
      return { ...state, usersLoading: action.loading };

    case 'SET_COUNTRY_BREAKDOWN':
      return { ...state, countryBreakdown: action.breakdown };

    case 'SET_COUNTRIES_LOADING':
      return { ...state, countriesLoading: action.loading };

    default:
      return state;
  }
}
